import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    DataService,
    NotificationService,
} from '@vendure/admin-ui/core';

import { ProductVariant } from '../generated';
import {
    BannerButtonData,
    ButtonActionType,
    NavigationTargetType,
    defaultBannerButtonData,
    NavContext,
    Page,
    PageComponentData,
    defaultNavigationItem,
    NavigationItem,
} from '../ui-types';
import { createNavContext, safeJSONParse } from '../utils';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { Observable, Subject, isObservable, takeUntil } from 'rxjs';

@Component({
    selector: 'banner-button-form',
    template: `
        <div class="clr-row" mb-2>
            <vdr-form-field class="clr-col-6">
                <clr-toggle-wrapper>
                    <input type="checkbox" clrToggle name="enabled" [formControl]="enabledControl" />
                    <label class="ml-1">{{ enabledControl.value ? 'Enabled' : 'Disabled' }}</label>
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>

        <div *ngIf="enabledControl.value" class="clr-row mt-2 mb-2">
            <vdr-form-field [label]="'Button Label'" class="clr-col-12">
                <vdr-dynamic-form-input [control]="textControl" [def]="textValueDef"></vdr-dynamic-form-input>
            </vdr-form-field>
        </div>

        <div class="clr-row mb-2" *ngIf="enabledControl.value">
            <div class="clr-col-6">
                <vdr-form-field [label]="'Label Text Size (px)'">
                    <input type="number" [formControl]="textSizeControl" min="0">
                </vdr-form-field>
            </div>
            <div class="clr-col-6">
                <vdr-form-field [label]="'Mobile Label Text Size (px)'">
                    <input type="number" [formControl]="mobileTextSizeControl" min="0">
                </vdr-form-field>
            </div>
        </div>

        <div class="clr-row mb-2" *ngIf="enabledControl.value">
            <div class="clr-col-6">
                <vdr-form-field [label]="'Label Text Color'">
                    <input type="color" [formControl]="textColorControl">
                </vdr-form-field>
            </div>
            <div class="clr-col-6">
                <vdr-form-field [label]="'Button Color'">
                    <input type="color" [formControl]="backgroundColorControl">
                </vdr-form-field>
            </div>
        </div>

        <div class="mb-2" *ngIf="enabledControl.value">
            <div class="clr-row">
                <vdr-form-field [label]="'Button Action'" class="clr-col-12 mb-2">
                    <select [formControl]="actionTypeControl">
                        <option value="navigate-to">Navigate To</option>
                        <option value="add-variant-to-cart">Add to Cart</option>
                    </select>
                </vdr-form-field>
            </div>

            <div *ngIf="actionTypeControl.value === ButtonActionType.AddVariantToCart" class="clr-row">
                <vdr-form-field [label]="'Select a variant to add to shopping cart'" class="clr-col-12">
                    <vdr-dynamic-form-input
                        [control]="selectedVariantControl"
                        [def]="{
                            type: 'relation',
                            entity: 'ProductVariant',
                            ui: { component: 'relation-form-input' }
                        }"
                    ></vdr-dynamic-form-input>
                </vdr-form-field>
            </div>
        </div>

        <div class="clr-row" *ngIf="enabledControl.value && actionTypeControl.value === ButtonActionType.NavigateTo">
            <vdr-form-field [label]="'Destination'" class="clr-col-12" style="width:100%">
                <vdr-dynamic-form-input [control]="navigationItemControl" [def]="navigationItemDef"></vdr-dynamic-form-input>
            </vdr-form-field>
        </div>
    `,
    styleUrls: ['../styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class BannerButtonInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    enabledControl = new FormControl();
    textControl = new FormControl();
    textSizeControl = new FormControl();
    mobileTextSizeControl = new FormControl();
    textColorControl = new FormControl();
    backgroundColorControl = new FormControl();
    actionTypeControl = new FormControl();
    selectedVariantControl = new UntypedFormControl();
    navigationItemControl = new FormControl('');

    bannerButtonData: BannerButtonData;
    ButtonActionType = ButtonActionType;
    navCtx: NavContext = createNavContext(false);

    private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
    private languageChanges$?: Observable<unknown>;
    private destroy$ = new Subject<void>();

    navigationItemDef = {
        type: 'string',
        ui: {
            component: 'navigation-item-input',
            options: {
                navContext: this.navCtx,
                hideLabel: true,
            },
        },
    };

    textValueDef: any = {
        type: 'string',
        ui: {
            component: 'text-value-input',
            args: {
                activeLanguageCode: this.activeLanguageCode,
                languageChanges$: this.languageChanges$,
            },
        },
    };

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
    ) {}

    ngOnInit() {
        const args = (this.config as any)?.ui?.args ?? {};
        const options = (this.config as any)?.ui?.options ?? {};

        if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
            this.activeLanguageCode = args.activeLanguageCode;
        }
        if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
            this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
        }

        this.textValueDef = {
            ...this.textValueDef,
            ui: { ...this.textValueDef.ui, args: { activeLanguageCode: this.activeLanguageCode, languageChanges$: this.languageChanges$ } },
        };

        this.setupNavContext(options);

        this.bannerButtonData = safeJSONParse<BannerButtonData>(
            this.formControl.value,
            this.notificationService,
            defaultBannerButtonData,
        )!;

        //this.bannerButtonData.navigationItem = normalizeNavigationItem(this.bannerButtonData.navigationItem ?? {});
        this.initializeFormControls();

        if (
            this.bannerButtonData?.actionType === ButtonActionType.AddVariantToCart &&
            this.bannerButtonData?.selectedVariantId
        ) {
            this.dataService.product
                .getProductVariant(this.bannerButtonData.selectedVariantId.toString())
                .mapSingle(data => data.productVariant)
                .pipe(takeUntil(this.destroy$))
                .subscribe(variant => {
                    this.selectedVariantControl.setValue(variant);
                });
        }
    }

    ngAfterViewInit() {
        this.enabledControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onEnabledChange);
        this.textControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTextChange);
        this.textSizeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTextSizeChange);
        this.mobileTextSizeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onMobileTextSizeChange);
        this.textColorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTextColorChange);
        this.backgroundColorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onBackgroundColorChange);
        this.actionTypeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onActionTypeChange);
        this.selectedVariantControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onSelectedVariantChange);
        this.navigationItemControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onNavigationItemChange);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupNavContext(options: any): void {
        const providedNavContext = options.navContext as NavContext | undefined;
        this.navCtx = providedNavContext ?? createNavContext(!!options.enableCurrentPage);
        this.navigationItemDef = {
            ...this.navigationItemDef,
            ui: {
                ...this.navigationItemDef.ui,
                options: {
                    navContext: this.navCtx,
                    hideLabel: true,
                },
            },
        };

        if (providedNavContext) {
            return;
        }

        const pushPages = (pages: Page[]) => {
            this.navCtx.pages$.next((pages ?? []) as Page[]);
        };

        if (options.pages$ && isObservable(options.pages$)) {
            options.pages$.pipe(takeUntil(this.destroy$)).subscribe((pages: any) => pushPages(pages ?? []));
        } else {
            pushPages((options.pages ?? []) as Page[]);
        }

        const pushComponents = (components: PageComponentData[]) => {
            this.navCtx.currentPageComponents$.next(components ?? []);
        };

        if (options.pageComponentChanges && isObservable(options.pageComponentChanges)) {
            options.pageComponentChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe((components: PageComponentData[]) => pushComponents(components ?? []));
        } else {
            pushComponents((options.currentPageComponents ?? []) as PageComponentData[]);
        }

        if (options.policyContentList$ && isObservable(options.policyContentList$)) {
            options.policyContentList$
                .pipe(takeUntil(this.destroy$))
                .subscribe((policyContentList: any) => this.navCtx.policyContentList$.next(policyContentList ?? []));
        } else if (Array.isArray(options.policyContentList)) {
            this.navCtx.policyContentList$.next(options.policyContentList ?? []);
        }
    }

    private initializeFormControls() {
        this.enabledControl.setValue(this.bannerButtonData?.enabled);
        this.textControl.setValue(this.toTextValueJson(this.bannerButtonData?.text));
        this.textSizeControl.setValue(this.bannerButtonData?.textSize);
        this.mobileTextSizeControl.setValue(this.bannerButtonData?.mobileTextSize);
        this.textColorControl.setValue(this.bannerButtonData?.textColor);
        this.backgroundColorControl.setValue(this.bannerButtonData?.backgroundColor);
        this.actionTypeControl.setValue(this.bannerButtonData?.actionType);
        this.navigationItemControl.setValue(JSON.stringify(this.bannerButtonData?.navigationItem ?? {}));
    }

    private onEnabledChange = (value: boolean) => {
        if (this.bannerButtonData?.enabled !== value) {
            this.bannerButtonData!.enabled = value;
            if (value === false) {
                this.bannerButtonData = {
                    ...defaultBannerButtonData,
                    enabled: false,
                    actionType: ButtonActionType.NavigateTo,
                    navigationItem: defaultNavigationItem//normalizeNavigationItem({ targetType: NavigationTargetType.SitePage }),
                };
                this.initializeFormControls();
            }
            this.updateJsonString();
        }
    };

    private onTextChange = (value: string) => {
        const next = this.parseTextValue(value);
        if (JSON.stringify(this.bannerButtonData?.text) !== JSON.stringify(next)) {
            this.bannerButtonData!.text = next;
            this.updateJsonString();
        }
    };

    private toTextValueJson(raw: any): string {
        if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
        return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
    }

    private parseTextValue(raw: string | null | undefined): any {
        if (!raw) return { format: 'plain', i18n: {} };
        try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
    }

    private onTextSizeChange = (value: number) => {
        if (this.bannerButtonData?.textSize !== value) {
            this.bannerButtonData!.textSize = value;
            this.updateJsonString();
        }
    };

    private onMobileTextSizeChange = (value: number) => {
        if (this.bannerButtonData?.mobileTextSize !== value) {
            this.bannerButtonData!.mobileTextSize = value;
            this.updateJsonString();
        }
    };

    private onTextColorChange = (value: string) => {
        if (this.bannerButtonData?.textColor !== value) {
            this.bannerButtonData!.textColor = value;
            this.updateJsonString();
        }
    };

    private onBackgroundColorChange = (value: string) => {
        if (this.bannerButtonData?.backgroundColor !== value) {
            this.bannerButtonData!.backgroundColor = value;
            this.updateJsonString();
        }
    };

    private onActionTypeChange = (value: ButtonActionType) => {
        if (this.bannerButtonData?.actionType !== value) {
            this.bannerButtonData!.actionType = value;
            if (value === ButtonActionType.AddVariantToCart) {
                this.bannerButtonData!.navigationItem = defaultNavigationItem;//normalizeNavigationItem({ targetType: NavigationTargetType.SitePage });
            } else if (value === ButtonActionType.NavigateTo) {
                this.bannerButtonData!.selectedVariantId = null;
            }
            this.updateJsonString();
        }
    };

    private onSelectedVariantChange = (value: ProductVariant) => {
        if (this.bannerButtonData?.actionType !== ButtonActionType.AddVariantToCart) {
            return;
        }
        const selectedVariantId = value ? value.id : null;
        if (this.bannerButtonData?.selectedVariantId !== selectedVariantId) {
            this.bannerButtonData!.selectedVariantId = selectedVariantId;
            this.updateJsonString();
        }
    };

    private onNavigationItemChange = (jsonValue: string | null) => {
        if (this.bannerButtonData?.actionType !== ButtonActionType.NavigateTo) {
            return;
        }
        let parsed = safeJSONParse<NavigationItem>(
            jsonValue ?? '',
            this.notificationService,
            defaultNavigationItem//normalizeNavigationItem({ targetType: NavigationTargetType.SitePage })
        )!;
        //const next = normalizeNavigationItem(parsed);
        if (JSON.stringify(this.bannerButtonData.navigationItem ?? {}) !== JSON.stringify(parsed)) {
            this.bannerButtonData.navigationItem = parsed;
            this.updateJsonString();
        }
    };

    private updateJsonString() {
        const jsonString = JSON.stringify(this.bannerButtonData);
        this.formControl.setValue(jsonString);
        this.formControl.markAsDirty();
    }
}
